import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Factory, RefreshCw, LogOut, QrCode, User, Loader2, CheckCircle2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'

type Item = {
  op_id: number; op_numero: number
  produto_codigo: string | null; produto_descricao: string
  variante: string; quantidade: string; quantidade_produzida: string
  etapa_op_id: number; etapa_codigo: string; etapa_nome: string
  status: string; op_status: string
  iniciada_em: string | null; pausada_em: string | null
  maquina_codigo: string | null; maquina_nome: string | null
  qr_token: string | null; fim_previsto: string | null
}

type Fila = {
  operador: { id: number; nome: string; maquina_padrao_id: number | null }
  minhas: Item[]
  minha_maquina: Item[]
  todas: Item[]
}

export default function MobileOperadorPage() {
  const nav = useNavigate()
  const { user, token, logout } = useAuth()
  const [fila, setFila] = useState<Fila | null>(null)
  const [loading, setLoading] = useState(true)
  const [scannerAberto, setScannerAberto] = useState(false)

  // Sem auth → /m/login (NÃO /login admin)
  useEffect(() => {
    if (!token) nav('/m/login', { replace: true })
  }, [token])

  async function refresh() {
    try {
      const r = await api.get<Fila>('/m/minha-fila')
      setFila(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])
  useEffect(() => {
    const iv = setInterval(refresh, 30_000)
    return () => clearInterval(iv)
  }, [])

  async function abrirOP(item: Item) {
    if (item.qr_token) {
      nav(`/m/op/${item.qr_token}`)
    } else {
      try {
        const r = await api.post(`/industrial/ordens/${item.op_id}/qr-token`)
        nav(`/m/op/${r.data.token}`)
      } catch (e: any) {
        toast.error(e?.response?.data?.detail || 'Erro')
      }
    }
  }

  function doLogout() {
    logout()
    nav('/m/login', { replace: true })
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-50">
      <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
    </div>
  }

  if (!fila) return null

  // Lista a mostrar: minhas + minha_maquina (sem "todas" — operador foca no que é dele)
  const lista = [...fila.minhas, ...fila.minha_maquina]

  return (
    <div className="min-h-screen bg-ink-50 pb-24">
      {/* Header limpo */}
      <header className="bg-gradient-to-br from-violet-700 to-blue-700 text-white p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <User className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-xs opacity-80 truncate">Operador</div>
              <div className="font-bold truncate">{fila.operador.nome}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={refresh} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={doLogout} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Botão escanear QR — protagonista */}
        <button
          onClick={() => setScannerAberto(true)}
          className="w-full h-20 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 active:scale-[0.98] text-white font-extrabold rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-violet-500/30 transition"
        >
          <QrCode className="h-8 w-8" />
          <div className="text-left">
            <div className="text-lg leading-tight">ESCANEAR QR</div>
            <div className="text-xs opacity-80 font-normal">Aponte pra etiqueta da OP</div>
          </div>
        </button>

        {/* Lista da minha máquina/etapa */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-sm font-bold text-ink-900 flex items-center gap-1">
              <Factory className="h-4 w-4 text-violet-600" />
              Pendentes na minha máquina
            </h2>
            <span className="text-xs text-ink-500 font-medium">{lista.length}</span>
          </div>

          {lista.length === 0 ? (
            <div className="bg-white rounded-2xl border border-ink-200 p-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
              <div className="font-bold text-ink-900">Nada pra produzir agora</div>
              <div className="text-xs text-ink-500 mt-1">Escaneie o QR de outra máquina ou aguarde</div>
            </div>
          ) : (
            <div className="space-y-2">
              {lista.map(it => (
                <ItemCard
                  key={it.etapa_op_id}
                  item={it}
                  destaque={fila.minhas.some(m => m.etapa_op_id === it.etapa_op_id)}
                  onClick={() => abrirOP(it)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {scannerAberto && (
        <ScannerModal
          onClose={() => setScannerAberto(false)}
          onScan={(url) => {
            setScannerAberto(false)
            const opMatch = url.match(/\/m\/op\/([^?#/]+)/)
            const maqMatch = url.match(/\/m\/maquina\/([^?#/]+)/)
            // Compat: QR antigo de pareamento usa /kiosk/<token> — também leva pra fila da máquina
            const kioskMatch = url.match(/\/kiosk\/([^?#/]+)/)
            if (opMatch) nav(`/m/op/${opMatch[1]}`)
            else if (maqMatch) nav(`/m/maquina/${maqMatch[1]}`)
            else if (kioskMatch) nav(`/m/maquina/${kioskMatch[1]}`)
            else toast.error('QR não reconhecido')
          }}
        />
      )}
    </div>
  )
}


function ItemCard({ item, destaque, onClick }: {
  item: Item; destaque?: boolean; onClick: () => void
}) {
  const pausada = !!item.pausada_em
  const cor =
    pausada ? 'border-amber-400 bg-amber-50' :
    item.status === 'em_execucao' ? 'border-emerald-400 bg-emerald-50' :
    'border-ink-200 bg-white'

  const statusLabel = pausada ? '⏸ pausada' :
    item.status === 'em_execucao' ? '▶ em execução' : '⏳ pendente'
  const statusCor = pausada ? 'bg-amber-200 text-amber-900' :
    item.status === 'em_execucao' ? 'bg-emerald-200 text-emerald-900' : 'bg-ink-200 text-ink-700'

  const atrasado = item.fim_previsto && new Date(item.fim_previsto) < new Date()

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-2xl border-2 p-3 active:opacity-70 ${cor} ${destaque ? 'shadow-lg ring-2 ring-emerald-300' : ''}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs font-mono text-ink-500">OP #{item.op_numero}</div>
        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${statusCor}`}>
          {statusLabel}
        </span>
      </div>
      <div className="font-bold text-base text-ink-900 line-clamp-2">{item.produto_descricao}</div>
      {item.produto_codigo && (
        <div className="text-xs font-mono text-ink-500 mt-0.5">{item.produto_codigo}</div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-700">
        <span className="font-semibold text-violet-700">📍 {item.etapa_nome}</span>
        {item.maquina_codigo && <span>🏭 {item.maquina_codigo}</span>}
        <span>📦 {Number(item.quantidade_produzida).toLocaleString('pt-BR')}/{Number(item.quantidade).toLocaleString('pt-BR')}</span>
        {atrasado && <span className="text-red-700 font-bold">⚠ ATRASADA</span>}
      </div>
    </button>
  )
}


function ScannerModal({ onClose, onScan }: { onClose: () => void; onScan: (text: string) => void }) {
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let scanner: any = null
    import(/* @vite-ignore */ 'html5-qrcode').then((mod) => {
      const Html5QrcodeScanner = mod.Html5QrcodeScanner
      scanner = new Html5QrcodeScanner(
        'qr-reader',
        { fps: 10, qrbox: { width: 240, height: 240 } },
        false,
      )
      scanner.render(
        (decodedText: string) => {
          scanner?.clear()
          onScan(decodedText)
        },
        () => {}
      )
    }).catch((e) => {
      setErro('Câmera indisponível: ' + (e?.message || ''))
    })
    return () => {
      try { scanner?.clear?.() } catch {}
    }
  }, [])

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-end justify-center">
      <div className="bg-white w-full rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold">Escanear QR</h3>
          <button onClick={onClose} className="text-ink-500 text-3xl">×</button>
        </div>
        <p className="text-sm text-ink-600 mb-3">Aponte pra etiqueta da OP ou da máquina</p>
        <div id="qr-reader" className="rounded-xl overflow-hidden"></div>
        {erro && <div className="mt-3 text-red-600 text-sm">{erro}</div>}
      </div>
    </div>
  )
}
